import { ApiError } from "@/services/api/api-client";

/** kebab-case, ≤100 chars — the backend's catalog key rule. */
export const LIBRARY_KEY_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** "Forklift Daily Pre-Op (2)" → "forklift-daily-pre-op-2". */
export function toLibraryKey(name: string): string {
	return name
		.toLowerCase()
		.normalize("NFKD")
		.replace(/\p{M}+/gu, "")
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "")
		.replace(/-{2,}/g, "-")
		.slice(0, 100)
		.replace(/-+$/g, "");
}

/** Trims and turns "" into undefined so the JSON body omits the key (null = unchanged). */
export function emptyToUndefined(
	value: string | null | undefined,
): string | undefined {
	const trimmed = value?.trim();
	return trimmed ? trimmed : undefined;
}

/**
 * Hands the viewer a JSON file built from an API response (no server file
 * endpoint exists — blob + object URL).
 */
export function downloadJsonFile(fileName: string, data: unknown): void {
	const blob = new Blob([JSON.stringify(data, null, 2)], {
		type: "application/json",
	});
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement("a");
	anchor.href = url;
	anchor.download = fileName;
	document.body.appendChild(anchor);
	anchor.click();
	anchor.remove();
	setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Pretty-prints a JSON string; returns the input untouched when it does not parse. */
export function prettyJson(raw: string): string {
	try {
		return JSON.stringify(JSON.parse(raw), null, 2);
	} catch {
		return raw;
	}
}

export function formatDate(value: string | null | undefined): string {
	if (!value) return "—";
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

export function formatDateTime(value: string | null | undefined): string {
	if (!value) return "—";
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

/**
 * Message + the server's per-item messages (ResultWrapper `messages[]` on
 * 400, FluentValidation errors on 422) for inline error panels.
 */
export function describeError(error: unknown): {
	message: string;
	details: string[];
} {
	if (error instanceof ApiError) {
		const details = Array.isArray(error.details)
			? error.details
			: error.details
				? Object.entries(error.details).map(
						([field, messages]) =>
							`${field}: ${messages.join("; ")}`,
					)
				: [];
		// Avoid repeating a single detail that already is the message.
		const distinct = details.filter((d) => d !== error.message);
		return { message: error.message, details: distinct };
	}
	if (error instanceof Error) return { message: error.message, details: [] };
	return { message: String(error), details: [] };
}
